import { useState, useEffect } from 'react';
import { Check, X, Loader2, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../UI/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../UI/dialog';
import { Drawer, DrawerContent } from '../UI/drawer';
import useIsMobile from '../../Hooks/useIsMobile';
import apiService from '../../Services/APIService';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';
import { getUserConfig } from '../../Store/Reducers';
import { TimeInputWithDialog } from './TimePickerWatch';
import { Label } from '../UI/label';
import { Popover, PopoverContent, PopoverTrigger } from '../UI/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../UI/tooltip';

// API to save Task EOD/Plan Times
const saveTaskTimesAPI = async (eodTime, planTime) => {
    const payload = [
        {EOD: eodTime, PLN: planTime}
    ];

    const result = await apiService.sendChange(4, 'profile', {
        updateType: 'TSKTIME',
        key: 'SETTIME',
        changeType: 'TSKTIME',
        targets: JSON.stringify(payload),
    });
    checkAPIResponse(result);
    return result;
};

const InfoHelpTooltip = ({ text, isMobile }) => {
    const triggerBtn = (
        <button type="button" className="inline-flex items-center justify-center focus:outline-none">
            <span className="info-icon">i</span>
            <span className="sr-only">Information</span>
        </button>
    );

    if (isMobile) {
        return (
            <Popover>
                <PopoverTrigger asChild>
                    {triggerBtn}
                </PopoverTrigger>
                <PopoverContent
                    side="top"
                    align="center"
                    sideOffset={4}
                    className="w-64 p-3 bg-white border border-gray-200 text-gray-900 rounded-md shadow-md z-[100]"
                >
                    <div className="space-y-1">
                        <p className="text-sm text-gray-600 leading-normal">{text}</p>
                    </div>
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    {triggerBtn}
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    align="center"
                    sideOffset={4}
                    className="w-64 px-3 py-2 bg-sahi-primary-color text-white rounded-md shadow-md z-[100] text-sm"
                >
                    <p className="leading-normal">{text}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const TaskTimesFormContent = ({ eodTime, setEodTime, planTime, setPlanTime, saving, isMobile }) => {
    return (
        <div className="flex flex-nowrap items-start gap-3 mt-1.5 w-full">
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5 min-h-[20px]">
                    <Label htmlFor="planTime" className="text-sm font-medium text-gray-700">Daily Plan</Label>
                    <InfoHelpTooltip 
                        text="Default suggestion time for task reminders." 
                        isMobile={isMobile} 
                    />
                </div>
                <TimeInputWithDialog
                    fieldId="planTime"
                    value={planTime}
                    onChange={setPlanTime}
                    disabled={saving}
                    isMobile={isMobile}
                    hideLabel={true}
                />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5 min-h-[20px]">
                    <Label htmlFor="eodTime" className="text-sm font-medium text-gray-700">End of Day (EOD)</Label>
                    <InfoHelpTooltip 
                        text="Default target completion time for tasks." 
                        isMobile={isMobile} 
                    />
                </div>
                <TimeInputWithDialog
                    fieldId="eodTime"
                    value={eodTime}
                    onChange={setEodTime}
                    disabled={saving}
                    isMobile={isMobile}
                    hideLabel={true}
                />
            </div>
        </div>
    );
};

const TaskTimesManager = ({ open, onOpenChange }) => {
    const isMobile = useIsMobile();
    const [eodTime, setEodTime] = useState('17:00');
    const [planTime, setPlanTime] = useState('10:00');
    const [saving, setSaving] = useState(false);

    // Load initial values from user configuration (V table)
    useEffect(() => {
        if (open) {
            const userConfig = getUserConfig();
            if (userConfig?.TaskConfig) {
                try {
                    const parsed = JSON.parse(userConfig.TaskConfig);
                    if (parsed && parsed[0]) {
                        setEodTime(parsed[0].EOD || '17:00');
                        setPlanTime(parsed[0].PLN || '10:00');
                    }
                } catch (err) {
                    console.error('Failed to parse TaskConfig:', err);
                }
            }
        }
    }, [open]);

    const handlePlanTimeChange = (newPlanTime) => {
        if (eodTime && newPlanTime > eodTime) {
            toast.error('Daily Plan time cannot be after EOD time.');
            return;
        }
        setPlanTime(newPlanTime);
    };

    const handleEodTimeChange = (newEodTime) => {
        if (planTime && newEodTime < planTime) {
            toast.error('EOD time cannot be before Daily Plan time.');
            return;
        }
        setEodTime(newEodTime);
    };

    const handleSave = async () => {
        if (!eodTime || !planTime) {
            toast.error('Please specify both EOD and Plan times.');
            return;
        }

        if (planTime > eodTime) {
            toast.error('Daily Plan time cannot be after EOD time.');
            return;
        }

        setSaving(true);
        try {
            await saveTaskTimesAPI(eodTime, planTime);
            toast.success('Task settings saved.');
            onOpenChange(false);
        } catch (err) {
            console.error('Failed to save Task settings:', err);
            toast.error(err.message || 'Failed to save preferences.');
        } finally {
            setSaving(false);
        }
    };

    const footerButtons = (
        <div className="flex gap-3 px-4 pb-4 pt-4 border-t border-zinc-100 shrink-0">
            <Button 
                onClick={handleSave} 
                disabled={saving} 
                className="sahi-primary-button flex-1 sm:flex-none"
            >
                {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <Check className="h-4 w-4" strokeWidth={3} />
                )}
                {saving ? "Saving..." : "Save Preferences"}
            </Button>
            <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)} 
                disabled={saving} 
                className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
            >
                <X className="w-3 h-3" /> Close
            </Button>
        </div>
    );

    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={onOpenChange}>
                <DrawerContent 
                    className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
                    style={{ height: 'auto', maxHeight: '85vh' }}
                >
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 h-16 shrink-0">
                            <div className="flex gap-4 items-center">
                                <button 
                                    type="button" 
                                    tabIndex={-1} 
                                    onClick={() => onOpenChange(false)}
                                    className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500"
                                >
                                    <ArrowLeft className="h-[24px] w-[24px]" />
                                </button>
                                <p className="text-lg font-semibold text-gray-900">Task Settings</p>
                            </div>
                        </div>
                        
                        <div className="overflow-y-auto custom-scroll flex-1 px-4 py-1">
                            <TaskTimesFormContent
                                eodTime={eodTime}
                                setEodTime={handleEodTimeChange}
                                planTime={planTime}
                                setPlanTime={handlePlanTimeChange}
                                saving={saving}
                                isMobile={true}
                            />
                        </div>
                        
                        {footerButtons}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:!max-w-[400px] flex flex-col gap-0 bg-[var(--background)] p-0 overflow-visible">
                <DialogHeader className="border-b px-4 py-3 flex-row items-center justify-between space-y-0">
                    <DialogTitle>Task Settings</DialogTitle>
                    <DialogClose asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <X className="h-4 w-4" />
                        </Button>
                    </DialogClose>
                </DialogHeader>
                
                <div className="px-4 py-3">
                    <TaskTimesFormContent
                        eodTime={eodTime}
                        setEodTime={handleEodTimeChange}
                        planTime={planTime}
                        setPlanTime={handlePlanTimeChange}
                        saving={saving}
                        isMobile={false}
                    />
                </div>
                
                {footerButtons}
            </DialogContent>
        </Dialog>
    );
};

export default TaskTimesManager;
